import type { Feature, LineString } from 'geojson';
import type { GeoJSONSource, Map as MapboxMap } from 'mapbox-gl';
import { LayerId, SourceId } from '../constants';

/** A point the dotted place link can start or end on. */
export interface PlaceLinkPoint {
  longitude: number;
  latitude: number;
}

/** Round caps turn the short dash into a dot. The gap is in multiples of the line width. */
const PLACE_LINK_DASH: [number, number] = [0.2, 2];
/** Brand mint. Reads over grey roads and the zone fill. */
const PLACE_LINK_COLOR = '#43F5BA';
const PLACE_LINK_WIDTH = 3;
/** The stored-place pin hides below this zoom. */
export const PLACE_PIN_MIN_ZOOM = 12;

export function placeLinkFeature(from: PlaceLinkPoint, to: PlaceLinkPoint): Feature<LineString> {
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: [
        [from.longitude, from.latitude],
        [to.longitude, to.latitude],
      ],
    },
  };
}

function putGeoJson(map: MapboxMap, sourceId: string, data: Feature): void {
  const existing = map.getSource(sourceId) as GeoJSONSource | undefined;
  if (existing) existing.setData(data);
  else map.addSource(sourceId, { type: 'geojson', data });
}

function dropGeoJson(map: MapboxMap, sourceId: string, layerId: string): void {
  if (map.getLayer(layerId)) map.removeLayer(layerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}

/** Draws or moves the dotted link. A later call with the same ids only updates the geometry. */
export function setPlaceLink(
  map: MapboxMap,
  sourceId: SourceId,
  layerId: LayerId,
  line: Feature<LineString>,
): void {
  putGeoJson(map, sourceId, line);
  if (map.getLayer(layerId)) return;
  map.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    slot: 'middle',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': PLACE_LINK_COLOR,
      'line-width': PLACE_LINK_WIDTH,
      'line-dasharray': PLACE_LINK_DASH,
    },
  });
}

export function removePlaceLink(map: MapboxMap, sourceId: SourceId, layerId: LayerId): void {
  dropGeoJson(map, sourceId, layerId);
}
